/**
 * Wishlist Enhancements
 * Handles wishlist UI helpers (counts, enquiry toggle, print/share actions).
 */

(function() {
  'use strict';

  let wishlistContentObserver = null;

  function formatPrice(value) {
    let amount = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(amount)) amount = 0;
    if (amount > 999) amount = amount / 100;
    return `$${amount.toFixed(2)}`;
  }

  function getFirstWishlistProductId() {
    const firstItem = document.querySelector('.tt-product-list[data-id]');
    return firstItem ? firstItem.getAttribute('data-id') : null;
  }

  async function fetchRecommendations(productId, limit = 3) {
    const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
    if (!response.ok) throw new Error('Failed to load recommendations');
    return response.json();
  }

  function renderRecommendation(product) {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageTag = image ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy">` : Shopify.assets?.noImageUrl || '';
    const price = product.price ? formatPrice(product.price) : '';
    return `
      <div class="suggested-product">
        <a href="${product.url}">
          <div class="suggested-product-image">${imageTag}</div>
          <div class="suggested-product-title">${product.title}</div>
          <div class="suggested-product-price">${price}</div>
        </a>
      </div>
    `;
  }

  async function loadWishlistRecommendations() {
    const section = document.querySelector('[data-wishlist-recommendations]');
    const grid = document.querySelector('[data-wishlist-recommendations-grid]');
    if (!section || !grid)
      return;

    const productId = getFirstWishlistProductId();
    if (!productId) {
      section.style.display = 'none';
      grid.innerHTML = '';
      grid.dataset.loaded = 'true';
      return;
    }

    if (grid.dataset.loaded === 'loading' || (grid.dataset.productId === productId && grid.dataset.loaded === 'true')) {
      section.style.display = grid.children.length ? 'block' : 'none';
      return;
    }

    grid.dataset.productId = productId;
    grid.dataset.loaded = 'loading';
    grid.innerHTML = '<div class="wishlist-recommendations-loading">Loading…</div>';

    try {
      const data = await fetchRecommendations(productId);
      const products = data && data.products ? data.products : [];
      if (!products.length) {
        grid.innerHTML = '';
        section.style.display = 'none';
        grid.dataset.loaded = 'true';
        return;
      }

      grid.innerHTML = products.map(renderRecommendation).join('');
      section.style.display = 'block';
      grid.dataset.loaded = 'true';
    } catch (error) {
      console.error('Wishlist recommendations error:', error);
      grid.innerHTML = '';
      section.style.display = 'none';
      grid.dataset.loaded = 'true';
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function ensureHeartIcon(titleParagraph) {
    if (titleParagraph.querySelector('.wishlist-heart-icon'))
      return;
    const heartIcon = document.createElement('span');
    heartIcon.className = 'wishlist-heart-icon';
    heartIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heart"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></svg>';
    titleParagraph.insertBefore(heartIcon, titleParagraph.firstChild || null);
  }

  function updateWishlistCount(titleParagraph) {
    if (!titleParagraph)
      return;

    let contentArea = document.querySelector('.tt-wishlist-details-content') ||
      document.querySelector('.tt-wishlist-details-popup') ||
      document.querySelector('.tab-content-inner-wrap');

    if (!contentArea)
      return;

    const count = contentArea.querySelectorAll('.tt-product-list').length;

    let countEl = titleParagraph.querySelector('.wishlist-count');
    if (!countEl) {
      countEl = document.createElement('span');
      countEl.className = 'wishlist-count';
      titleParagraph.appendChild(countEl);
    }
    countEl.textContent = ` ${count}`;
  }

  let formsSyncTimeout = null;
  let formsSyncAttempts = 0;

  const adminStoreHandle = window.shopifyAdminStoreHandle || (window.Shopify && window.Shopify.shop ? window.Shopify.shop.replace('.myshopify.com', '') : 'cassbrothers');

  function buildAdminUrl(id) {
    if (!id)
      return '';
    return `https://admin.shopify.com/store/${adminStoreHandle}/products/${id}`;
  }

  function collectWishlistItems() {
    return Array.from(document.querySelectorAll('.tt-product-list')).map(item => {
      // Try data-id on the item itself first
      let id = item.getAttribute('data-id') || '';
      // If not found, look for it on inner elements (ATC button or remove button)
      if (!id) {
        const innerEl = item.querySelector('[data-id]');
        id = innerEl ? innerEl.getAttribute('data-id') : '';
      }
      if (!id)
        return null;
      return {
        id,
        admin_url: buildAdminUrl(id)
      };
    }).filter(Boolean);
  }

  function ensureHiddenField(form, name, value) {
    if (!form)
      return;
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.wishlistField = 'true';
      form.appendChild(input);
    }
    input.value = value;
  }

  function getWishlistForms() {
    const container = document.querySelector('.wishlist-enquiry-form');
    if (!container)
      return [];

    const forms = new Set(container.querySelectorAll('form'));

    const potentialHosts = new Set([container, ...container.querySelectorAll('*')]);
    potentialHosts.forEach(host => {
      if (host && host.shadowRoot) {
        host.shadowRoot.querySelectorAll('form').forEach(form => forms.add(form));
      }
    });

    return Array.from(forms);
  }

  function fixFormLabelsInShadowDOM() {
    const formRoots = document.querySelectorAll('[data-form-root="true"]');
    formRoots.forEach(root => {
      const shadowHost = root.querySelector('shopify-forms-embed');
      if (shadowHost && shadowHost.shadowRoot) {
        const existingStyle = shadowHost.shadowRoot.querySelector('#custom-label-fix');
        if (!existingStyle) {
          const style = document.createElement('style');
          style.id = 'custom-label-fix';
          style.textContent = `
            ._formFieldContainer_1mxsl_5,
            [class*="_formFieldContainer"] {
              display: flex !important;
              flex-direction: column !important;
              gap: 6px !important;
            }
            ._formInputFieldLabel_1mxsl_38,
            [class*="_formInputFieldLabel"] {
              position: static !important;
              transform: none !important;
              top: auto !important;
              left: auto !important;
              font-size: 14px !important;
              font-weight: 500 !important;
              color: #374151 !important;
              order: -1 !important;
              pointer-events: auto !important;
              opacity: 1 !important;
              background: transparent !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            ._formInputField_nag3b_7,
            [class*="_formInputField"],
            ._formPhoneInputField_piv1u_17,
            [class*="_formPhoneInputField"],
            ._textArea_17mgw_1,
            [class*="_textArea"] {
              padding: 12px !important;
            }
            ._formPhoneInputContainer_piv1u_6,
            [class*="_formPhoneInputContainer"] {
              display: flex !important;
              flex-direction: column !important;
              gap: 6px !important;
            }
            ._formPhoneInputContainer_piv1u_6 > label,
            [class*="_formPhoneInputContainer"] > label {
              position: static !important;
              transform: none !important;
              order: -1 !important;
            }
            .phone-country-selector {
              order: 0 !important;
            }
            ._formPhoneInputContainer_piv1u_6 ._formFieldContainer_1mxsl_5,
            [class*="_formPhoneInputContainer"] [class*="_formFieldContainer"] {
              flex-direction: row !important;
              align-items: center !important;
            }
            ._formPhoneInputContainer_piv1u_6 ._formFieldContainer_1mxsl_5 label,
            [class*="_formPhoneInputContainer"] [class*="_formFieldContainer"] label {
              display: none !important;
            }
          `;
          shadowHost.shadowRoot.appendChild(style);
        }
      }
    });
  }

  function hideCustomProductTextarea() {
    // Try Shadow DOM (Shopify Forms app)
    const shopifyFormsEmbed = document.querySelector('shopify-forms-embed');
    if (shopifyFormsEmbed && shopifyFormsEmbed.shadowRoot) {
      const customTextarea = shopifyFormsEmbed.shadowRoot.querySelector('textarea[name="custom#product"], textarea[name="custom#products"]');
      if (customTextarea) {
        customTextarea.style.setProperty('display', 'none', 'important');
        customTextarea.setAttribute('aria-hidden', 'true');
        customTextarea.setAttribute('tabindex', '-1');
        const wrapper = customTextarea.closest('._formFieldContainer_1mxsl_5, [class*="_formFieldContainer"]');
        if (wrapper) {
          wrapper.style.setProperty('display', 'none', 'important');
          wrapper.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  function updateExternalWishlistForms() {
    const forms = getWishlistForms();
    if (!forms.length) {
      if (formsSyncAttempts < 6) {
        formsSyncAttempts += 1;
        setTimeout(updateExternalWishlistForms, 400);
      }
      return;
    }

    // Always hide the custom textarea immediately
    hideCustomProductTextarea();

    // Wait for wishlist content to load (TT Wishlist app loads products async)
    const items = collectWishlistItems();
    if (!items.length) {
      if (formsSyncAttempts < 10) {
        formsSyncAttempts += 1;
        setTimeout(updateExternalWishlistForms, 500);
      }
      return;
    }

    formsSyncAttempts = 0;
    fixFormLabelsInShadowDOM();
    const jsonPayload = JSON.stringify(items);
    const idsPayload = items.map(item => item.id).filter(Boolean).join(',');
    const adminLinksPayload = items.map(item => item.admin_url).filter(Boolean).join('\n');

    forms.forEach(form => {
      ensureHiddenField(form, 'fields[wishlist_items_json]', jsonPayload);
      ensureHiddenField(form, 'fields[wishlist_items]', idsPayload);
      ensureHiddenField(form, 'fields[wishlist_product_ids]', idsPayload);
      ensureHiddenField(form, 'fields[wishlist_admin_links]', adminLinksPayload);
      ensureHiddenField(form, 'wishlist_items_json', jsonPayload);
      ensureHiddenField(form, 'wishlist_items', idsPayload);
      ensureHiddenField(form, 'wishlist_admin_links', adminLinksPayload);

      // Try regular DOM first
      let customTextarea = form.querySelector('textarea[name="custom#product"], textarea[name="custom#products"]');

      // If not found, try Shadow DOM (Shopify Forms app)
      if (!customTextarea) {
        const shopifyFormsEmbed = document.querySelector('shopify-forms-embed');
        if (shopifyFormsEmbed && shopifyFormsEmbed.shadowRoot) {
          customTextarea = shopifyFormsEmbed.shadowRoot.querySelector('textarea[name="custom#product"], textarea[name="custom#products"]');
        }
      }

      if (customTextarea) {
        try {
          customTextarea.value = adminLinksPayload;
          customTextarea.defaultValue = adminLinksPayload;
          customTextarea.dispatchEvent(new Event('input', { bubbles: true }));
          customTextarea.dispatchEvent(new Event('change', { bubbles: true }));

          // Hide the textarea
          customTextarea.style.setProperty('display', 'none', 'important');
          customTextarea.setAttribute('aria-hidden', 'true');
          customTextarea.setAttribute('tabindex', '-1');

          // Hide the wrapper
          const wrapper = customTextarea.closest('._formFieldContainer_1mxsl_5, [class*="_formFieldContainer"]');
          if (wrapper) {
            wrapper.style.setProperty('display', 'none', 'important');
            wrapper.setAttribute('aria-hidden', 'true');
          }
        } catch (error) {
          console.debug('Wishlist custom textarea sync error:', error);
        }
      }
    });
  }

  function scheduleWishlistFormSync(delay = 200) {
    clearTimeout(formsSyncTimeout);
    formsSyncTimeout = setTimeout(() => {
      formsSyncAttempts = 0;
      updateExternalWishlistForms();
    }, delay);
  }

  function handleButtonActions(buttonsContainer, enquirySection) {
    if (!buttonsContainer || buttonsContainer.dataset.enhanced === 'true')
      return;

    const enquiryBtn = buttonsContainer.querySelector('.wishlist-enquiry-btn');
    const printBtn = buttonsContainer.querySelector('.wishlist-print-btn');
    const shareBtn = buttonsContainer.querySelector('.wishlist-share-btn');

    if (enquiryBtn && enquirySection) {
      enquiryBtn.addEventListener('click', () => {
        const isVisible = enquirySection.classList.toggle('is-visible');
        enquirySection.style.display = isVisible ? 'block' : 'none';
        enquiryBtn.textContent = isVisible ? 'Hide Enquiry' : 'Send Enquiry';
        if (isVisible) {
          setTimeout(() => enquirySection.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
          scheduleWishlistFormSync(100);
        }
      });
    }

    if (printBtn) {
      printBtn.addEventListener('click', async () => {
        const wishlistContent = document.querySelector('.tt-wishlist-details-main');
        if (!wishlistContent)
          return;

        const printWindow = window.open('', '_blank');
        if (!printWindow)
          return;

        const items = Array.from(wishlistContent.querySelectorAll('.tt-product-list'));
        const formatOptions = {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        };
        const today = new Date();
        const formattedDate = today.toLocaleDateString(undefined, formatOptions);
        const validUntilDate = new Date(today);
        validUntilDate.setDate(validUntilDate.getDate() + 7);
        const formattedValidUntil = validUntilDate.toLocaleDateString(undefined, formatOptions);

        const productCache = new Map();
        const escapeHtml = value => {
          if (typeof value !== 'string')
            return '';
          return value.replace(/[&<>"']/g, char => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            '\'': '&#39;'
          })[char]);
        };

        const resolveSku = async item => {
          const handle = item.getAttribute('data-handle');
          if (!handle)
            return '';

          if (!productCache.has(handle)) {
            try {
              const response = await fetch(`/products/${handle}.js`);
              if (!response.ok)
                throw new Error(`Wishlist print product fetch failed for ${handle}`);
              const data = await response.json();
              productCache.set(handle, data);
            } catch (error) {
              console.error(error);
              productCache.set(handle, null);
            }
          }

          const productData = productCache.get(handle);
          if (!productData || !Array.isArray(productData.variants))
            return '';

          const select = item.querySelector('select');
          const selectedVariantId = select?.value;
          const variant = selectedVariantId
            ? productData.variants.find(v => String(v.id) === String(selectedVariantId))
            : productData.variants[0];

          return typeof variant?.sku === 'string' ? variant.sku.trim() : '';
        };

        const cards = (await Promise.all(items.map(async item => {
          const link = item.querySelector('.tt-product-title');
          const href = link?.getAttribute('href') || '#';
          const title = link?.textContent?.trim() || 'Wishlist item';
          const safeTitle = escapeHtml(title);
          const priceWrap = item.querySelector('.tt-product-price-wrap');
          let priceText = '';
          let comparePriceText = '';
          if (priceWrap) {
            const compareEl = priceWrap.querySelector('.compare-at-price');
            if (compareEl) {
              comparePriceText = compareEl.textContent?.trim() || '';
            }
            const priceNode = Array.from(priceWrap.childNodes || []).find(function(node) {
              return node.nodeType === 3 && node.textContent && node.textContent.trim();
            });
            if (priceNode && priceNode.textContent) {
              priceText = priceNode.textContent.trim();
            } else {
              const priceEl = Array.from(priceWrap.querySelectorAll(':scope > *')).find(function(el) {
                return !el.classList.contains('compare-at-price');
              });
              priceText = priceEl?.textContent?.trim() || '';
            }
            if (!priceText) {
              priceText = priceWrap.textContent?.trim() || '';
              if (priceText && comparePriceText) {
                priceText = priceText.replace(comparePriceText, '').trim();
              }
            }
          }
          const safePrice = escapeHtml(priceText);
          const safeComparePrice = escapeHtml(comparePriceText);
          const imageEl = item.querySelector('.tt-image-wrap img');
          const imageSrc = imageEl?.getAttribute('src') || '';
          const imageAlt = imageEl?.getAttribute('alt') || title;
          const safeImageAlt = escapeHtml(imageAlt);
          const sku = escapeHtml(await resolveSku(item));
          if (!link && !safePrice && !sku && !imageSrc)
            return '';

          return `
            <div class="wishlist-print-card">
              ${
                imageSrc
                  ? `<div class="wishlist-print-media">
                      <img src="${imageSrc}" alt="${safeImageAlt}">
                    </div>`
                  : ''
              }
              <div class="wishlist-print-body">
                <a class="wishlist-print-title" href="${href}">${safeTitle}</a>
                ${sku ? `<div class="wishlist-print-sku">SKU: ${sku}</div>` : ''}
                ${
                  safePrice
                    ? `<div class="wishlist-print-price">
                        <span class="wishlist-print-price-current">${safePrice}</span>
                        ${
                          safeComparePrice && safeComparePrice !== safePrice
                            ? `<span class="wishlist-print-price-compare">${safeComparePrice}</span>`
                            : ''
                        }
                      </div>`
                    : ''
                }
              </div>
            </div>`;
        }))).filter(Boolean).join('');

        const customerName = escapeHtml(window.customerName || '');
        const disclaimer = `All prices valid until ${escapeHtml(formattedValidUntil)}`;
        const cardsMarkup = cards || '<p class="wishlist-print-empty">No wishlist items available.</p>';
        printWindow.document.write(`<!DOCTYPE html><html><head><title>My Wishlist</title>
          <style>
            :root {
              color-scheme: light;
              font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
            }
            body {
              margin: 0;
              padding: 32px;
              background: #fff;
              color: #111;
            }
            h1 {
              margin: 0;
              font-size: 28px;
              font-weight: 600;
              letter-spacing: 0.01em;
            }
            .wishlist-print-wrapper {
              max-width: 900px;
              margin: 0 auto;
            }
            .wishlist-print-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 32px;
            }
            .wishlist-print-meta {
              font-size: 13px;
              text-align: right;
              color: #5c5c5c;
              line-height: 1.45;
            }
            .wishlist-print-grid {
              display: flex;
              flex-direction: column;
              gap: 20px;
            }
            .wishlist-print-card {
              border: 1px solid #e2e2e2;
              border-radius: 12px;
              overflow: hidden;
              display: flex;
              flex-direction: row;
              gap: 20px;
              padding: 20px;
              background: #fff;
              page-break-inside: avoid;
            }
            .wishlist-print-media {
              flex: 0 0 100px;
              max-width: 100px;
              border-radius: 10px;
              overflow: hidden;
              background: #f5f5f5;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .wishlist-print-media img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }
            @media print {
              .wishlist-print-card {
                break-inside: avoid;
              }
            }
            .wishlist-print-body {
              flex: 1 1 auto;
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            .wishlist-print-title {
              font-size: 17px;
              font-weight: 600;
              line-height: 1.35;
              color: #181818;
              text-decoration: none;
            }
            .wishlist-print-title:hover {
              text-decoration: underline;
            }
            .wishlist-print-price {
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 15px;
              color: #444;
            }
            .wishlist-print-price-current {
              font-weight: 600;
            }
            .wishlist-print-price-compare {
              font-weight: 400;
              color: #6b6b6b;
              text-decoration: line-through;
            }
            .wishlist-print-sku {
              font-size: 13px;
              font-weight: 500;
              color: #5c5c5c;
            }
            .wishlist-print-footer {
              margin-top: 40px;
              font-size: 13px;
              text-align: center;
              color: #6b6b6b;
            }
            .wishlist-print-disclaimer {
              margin: 0 0 28px;
              font-size: 13px;
              color: #5c5c5c;
            }
            .wishlist-print-empty {
              font-size: 15px;
              color: #5c5c5c;
            }
            .wishlist-action-buttons,
            .tt-product-action,
            .tt-qty-selector,
            .tt-tab-header-wrap,
            .wishlist-suggested-products,
            .wishlist-enquiry-form {
              display: none !important;
            }
            @media print {
              body { padding: 24px; }
              .wishlist-print-wrapper { max-width: 100%; }
            }
          </style>
        </head><body>
          <div class="wishlist-print-wrapper">
            <div class="wishlist-print-header">
              <h1>My Wishlist</h1>
              <div class="wishlist-print-meta">
                <div>${formattedDate}</div>
                <div>${customerName}</div>
              </div>
            </div>
            <div class="wishlist-print-disclaimer">${disclaimer}</div>
            <div class="wishlist-print-grid">${cardsMarkup}</div>
            <div class="wishlist-print-footer">Generated from ${window.location.origin}</div>
          </div>
        </body></html>`);
        printWindow.document.close();
        printWindow.print();
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', async () => {
        const shareData = {
          title: 'My Wishlist',
          text: 'Check out my wishlist!',
          url: window.location.href
        };

        try {
          if (navigator.share) {
            await navigator.share(shareData);
          } else {
            await navigator.clipboard.writeText(window.location.href);
            const original = shareBtn.textContent;
            shareBtn.textContent = 'Link Copied!';
            shareBtn.style.background = '#28a745';
            setTimeout(() => {
              shareBtn.textContent = original;
              shareBtn.style.background = '';
            }, 2000);
          }
        } catch (error) {
          const shareUrl = encodeURIComponent(window.location.href);
          const shareText = encodeURIComponent('Check out my wishlist!');
          const popup = document.createElement('div');
          popup.innerHTML = `
            <div class="share-overlay"></div>
            <div class="share-popup">
              <h4>Share Wishlist</h4>
              <div class="share-options">
                <a href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}" target="_blank" class="share-facebook">Share on Facebook</a>
                <a href="https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}" target="_blank" class="share-twitter">Share on Twitter</a>
                <button class="share-copy">Copy Link</button>
                <button class="share-close">Close</button>
              </div>
            </div>`;

          const copyBtn = popup.querySelector('.share-copy');
          const closeBtn = popup.querySelector('.share-close');
          const overlay = popup.querySelector('.share-overlay');

          copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(window.location.href).then(() => {
              copyBtn.textContent = 'Copied!';
              setTimeout(() => popup.remove(), 1000);
            });
          });
          [closeBtn, overlay].forEach(el => el.addEventListener('click', () => popup.remove()));
          document.body.appendChild(popup);
        }
      });
    }

    buttonsContainer.dataset.enhanced = 'true';
  }

  function handleEnquirySubmission(form) {
    if (!form || form.dataset.enhanced === 'true')
      return;

    form.addEventListener('submit', async event => {
      event.preventDefault();

      const successMessage = form.querySelector('.enquiry-success-message');
      const errorMessage = form.querySelector('.enquiry-error-message');
      const submitButton = form.querySelector('button[type="submit"]');

      successMessage?.classList.remove('show');
      errorMessage?.classList.remove('show');

      const originalText = submitButton.textContent;
      submitButton.textContent = 'Submitting...';
      submitButton.disabled = true;

      try {
        if (window.Shopify?.recaptchaV3) {
          await Shopify.recaptchaV3.addToken(form, 'contact');
        }

        const formData = new FormData(form);
        const params = new URLSearchParams();
        params.set('form_type', 'contact');
        params.set('utf8', '✓');

        const name = formData.get('name') || '';
        const email = formData.get('email') || '';
        const phone = formData.get('phone') || '';
        const postcode = formData.get('postcode') || '';
        const message = formData.get('message') || '';
        const tags = formData.get('contact[tags]') || 'wishlist-enquiry';

        params.set('contact[name]', name);
        params.set('contact[email]', email);
        if (phone)
          params.set('contact[phone]', phone);
        if (tags)
          params.set('contact[tags]', tags);

        let bodyContent = 'Wishlist enquiry';
        if (postcode)
          bodyContent += ` (Postcode: ${postcode})`;
        if (message)
          bodyContent += `\n\n${message}`;
        params.set('contact[body]', bodyContent);

        const recaptchaToken = formData.get('g-recaptcha-response');
        if (recaptchaToken)
          params.set('g-recaptcha-response', recaptchaToken);

        const response = await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          body: params.toString()
        });

        if (!response.ok)
          throw new Error('Server error');

        successMessage?.classList.add('show');
        form.reset();
        successMessage?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        console.error('Enquiry submission error:', error);
        errorMessage?.classList.add('show');
      } finally {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
      }
    });

    form.dataset.enhanced = 'true';
  }

  // Add discount badges and SKUs to wishlist items
  function addDiscountBadges() {
    // Try multiple selectors - the wishlist app structure can vary
    let productItems = document.querySelectorAll('.tt-wishlist-details-popup .tt-product-list');
    if (!productItems.length) {
      productItems = document.querySelectorAll('.tt-wishlist-details-content .tt-product-list');
    }
    if (!productItems.length) {
      productItems = document.querySelectorAll('.tt-wishlist-details .tt-product-list');
    }
    productItems.forEach(async (item) => {
      const priceWrap = item.querySelector('.tt-product-price-wrap');
      if (!priceWrap) return;

      // Skip if already processed
      if (priceWrap.dataset.enhanced === 'true') return;
      priceWrap.dataset.enhanced = 'true';

      // Get product handle for SKU lookup - try multiple attributes
      let handle = item.dataset.handle || item.getAttribute('data-handle');
      // Also try getting handle from title link
      if (!handle) {
        const titleLink = item.querySelector('.tt-product-title');
        if (titleLink && titleLink.href) {
          const match = titleLink.href.match(/\/products\/([^/?]+)/);
          handle = match ? match[1] : null;
        }
      }

      // Add discount badge if applicable
      const compareAtPrice = priceWrap.querySelector('.compare-at-price');
      if (compareAtPrice) {
        const compareText = compareAtPrice.textContent.trim();
        if (compareText && compareText !== '$0.00') {
          const currentPriceText = Array.from(priceWrap.childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE ||
                           (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('compare-at-price') && !node.classList.contains('prod-savings') && !node.classList.contains('prod-sku')))
            .map(node => node.textContent.trim())
            .join('')
            .trim();

          const parsePrice = (str) => {
            const match = str.match(/[\d,]+\.?\d*/);
            return match ? parseFloat(match[0].replace(/,/g, '')) : 0;
          };

          const currentPrice = parsePrice(currentPriceText);
          const comparePrice = parsePrice(compareText);

          if (currentPrice > 0 && comparePrice > 0 && currentPrice < comparePrice) {
            const discountPercent = Math.round(((comparePrice - currentPrice) / comparePrice) * 100);
            const badge = document.createElement('p');
            badge.className = 'prod-savings';
            badge.innerHTML = `<span class="prod-savings__content">Save ${discountPercent}%</span>`;
            priceWrap.appendChild(badge);
          }
        }
      }

      // Fetch and add SKU (insert after title, before price)
      if (handle) {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (response.ok) {
            const product = await response.json();
            const sku = product.variants[0]?.sku;
            if (sku) {
              const skuEl = document.createElement('p');
              skuEl.className = 'prod-sku wishlist-sku';
              skuEl.innerHTML = `<span>SKU:</span> <span>${sku}</span>`;
              // Insert SKU after title (before price wrap)
              const productContent = item.querySelector('.tt-product-content');
              const titleEl = productContent?.querySelector('.tt-product-title');
              if (titleEl && titleEl.nextSibling) {
                titleEl.parentNode.insertBefore(skuEl, titleEl.nextSibling);
              } else if (productContent) {
                productContent.insertBefore(skuEl, priceWrap);
              }
            }
          }
        } catch (e) {
          // Silently fail if SKU fetch fails
        }
      }
    });
  }

  function initWishlistEnhancements() {
    // Try both selectors - the app uses different classes in different contexts
    const popup = document.querySelector('.tt-wishlist-details-popup') ||
                  document.querySelector('.tt-wishlist-details');
    if (!popup)
      return;

    popup.classList.add('drawer');

    const titleContainer = popup.querySelector('.tt-details-title');
    const titleParagraph = titleContainer?.querySelector('p');

    if (titleParagraph) {
      titleParagraph.classList.add('wishlist-title-text');
      ensureHeartIcon(titleParagraph);
      updateWishlistCount(titleParagraph);
    }

    const buttonsContainer = titleContainer?.querySelector('.wishlist-action-buttons');
    const enquirySection = popup.querySelector('.wishlist-enquiry-form');

    if (buttonsContainer && enquirySection)
      handleButtonActions(buttonsContainer, enquirySection);

    const enquiryForm = enquirySection?.querySelector('.enquiry-form');
    if (enquiryForm)
      handleEnquirySubmission(enquiryForm);

    // Add discount badges to prices (with retries for async-loaded products)
    addDiscountBadges();
    setTimeout(addDiscountBadges, 500);
    setTimeout(addDiscountBadges, 1000);
    setTimeout(addDiscountBadges, 2000);

    loadWishlistRecommendations();
    scheduleWishlistFormSync(80);
  }

  function observeWishlistContent() {
    const popup = document.querySelector('.tt-wishlist-details-popup');
    const content = popup?.querySelector('.tt-wishlist-details-content');
    if (!content)
      return;

    const titleParagraph = document.querySelector('.wishlist-title-text');
    if (!titleParagraph)
      return;

    if (wishlistContentObserver)
      wishlistContentObserver.disconnect();

    wishlistContentObserver = new MutationObserver(() => {
      setTimeout(() => {
        updateWishlistCount(titleParagraph);
        addDiscountBadges();
        loadWishlistRecommendations();
        scheduleWishlistFormSync(120);
      }, 300);
    });

    wishlistContentObserver.observe(content, { childList: true, subtree: true });
  }

  ready(() => {
    initWishlistEnhancements();
    observeWishlistContent();
    scheduleWishlistFormSync(120);

    const popupObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || !mutation.addedNodes.length)
          continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && (node.classList?.contains('tt-wishlist-details-popup') || node.querySelector?.('.tt-wishlist-details-popup'))) {
            setTimeout(() => {
              initWishlistEnhancements();
              observeWishlistContent();
              loadWishlistRecommendations();
              scheduleWishlistFormSync(160);
            }, 500);
            return;
          }
        }
      }
    });

    popupObserver.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener('click', event => {
    if (event.target.closest('[class*="wishlist"]') || event.target.closest('[data-wishlist]')) {
      setTimeout(() => {
        initWishlistEnhancements();
        observeWishlistContent();
        loadWishlistRecommendations();
        scheduleWishlistFormSync(200);
      }, 600);
    }
  });

  // Also observe for when wishlist drawer becomes active/visible
  const drawerObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target;
        if (target.classList && target.classList.contains('tt-wishlist-details-popup') && target.classList.contains('active')) {
          setTimeout(addDiscountBadges, 300);
          setTimeout(addDiscountBadges, 800);
        }
      }
    }
  });

  // Start observing for class changes on wishlist popup
  const startObservingDrawer = () => {
    const popup = document.querySelector('.tt-wishlist-details-popup');
    if (popup) {
      drawerObserver.observe(popup, { attributes: true, attributeFilter: ['class'] });
    } else {
      // Popup might not exist yet, observe body for it
      const bodyObserver = new MutationObserver((mutations, obs) => {
        const popup = document.querySelector('.tt-wishlist-details-popup');
        if (popup) {
          drawerObserver.observe(popup, { attributes: true, attributeFilter: ['class'] });
          obs.disconnect();
        }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
  };

  startObservingDrawer();

  // Open wishlist drawer when item is added
  function openWishlistDrawer() {
    const popup = document.querySelector('.tt-wishlist-details-popup');
    if (popup) {
      popup.classList.add('active');
      document.body.classList.add('tt-overflow-hidden');
    }
  }

  // Listen for wishlist add button clicks
  document.addEventListener('click', event => {
    const wishlistBtn = event.target.closest('.wishlist-plp-btn-wrap, .wishlist-pdp-btn-wrap');
    if (wishlistBtn && !wishlistBtn.classList.contains('active')) {
      // Button is not active, so this is an ADD action
      setTimeout(() => {
        openWishlistDrawer();
        initWishlistEnhancements();
        observeWishlistContent();
      }, 800);
    }
  });
})();
